/**
 * WaitlistCard - iOS waitlist prompt for Dashboard
 *
 * Shows a compact card prompting users to join the iOS app waitlist.
 * Dismissible and remembers state in localStorage.
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  submitWaitlistEntry,
  USE_CASE_OPTIONS,
  INDUSTRY_OPTIONS
} from '../services/waitlistService';

export default function WaitlistCard() {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: user?.email || '',
    usageType: 'professional',
    useCase: '',
    industry: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    // Check if dismissed or already joined
    const dismissed = localStorage.getItem('dashboard_waitlist_dismissed');
    const joined = localStorage.getItem('waitlist_joined');
    if (dismissed || joined) {
      setIsDismissed(true);
    }
  }, []);

  // Pre-fill user data when userProfile loads
  useEffect(() => {
    if (userProfile) {
      setFormData(prev => ({
        ...prev,
        firstName: prev.firstName || userProfile.firstName || '',
        lastName: prev.lastName || userProfile.lastName || ''
      }));
    }
  }, [userProfile]);

  // Show waitlist status card if user is on the waitlist
  if (userProfile?.waitlistStatus) {
    const status = userProfile.waitlistStatus;
    const isApproved = status === 'approved';
    const isPending = status === 'pending';

    return (
      <div className="rounded-2xl overflow-hidden mb-6 bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 dark:border-green-500/20">
        <div className="p-4 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-gradient-to-br from-green-500 to-emerald-600">
            {isApproved ? (
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <p className="text-[#1d1d1f] dark:text-white font-semibold">
                {isApproved ? "You're approved!" : "Congrats! You're on the iOS waitlist"}
              </p>
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                isApproved
                  ? 'bg-green-500/20 text-green-600 dark:text-green-400'
                  : 'bg-green-500/20 text-green-600 dark:text-green-400'
              }`}>
                {isApproved ? 'Approved' : 'Confirmed'}
              </span>
            </div>
            <p className="text-[#6e6e73] dark:text-white/60 text-sm">
              {isApproved
                ? "Download QuickMeet for iOS and sign in with your account."
                : "Keep an eye on your inbox - we'll send you an email with next steps when it's your turn!"
              }
            </p>
          </div>
          {isApproved && (
            <a
              href="https://apps.apple.com/app/quickmeet"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 rounded-xl bg-[#1d1d1f] dark:bg-white text-white dark:text-[#1d1d1f] text-sm font-medium hover:opacity-90 transition-opacity flex items-center gap-2 flex-shrink-0"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
              </svg>
              Get App
            </a>
          )}
        </div>
      </div>
    );
  }

  // Don't show if dismissed
  if (isDismissed) {
    return null;
  }

  const handleDismiss = () => {
    localStorage.setItem('dashboard_waitlist_dismissed', 'true');
    setIsDismissed(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      if (!formData.useCase || !formData.industry) {
        throw new Error('Please select use case and industry');
      }

      // Use userProfile data for name, fallback to formData
      const submitData = {
        ...formData,
        firstName: formData.firstName || userProfile?.firstName || 'User',
        lastName: formData.lastName || userProfile?.lastName || '',
        email: user?.email || formData.email
      };

      await submitWaitlistEntry(submitData, user?.uid);

      localStorage.setItem('waitlist_joined', 'true');
      setIsSuccess(true);

      // Hide after success
      setTimeout(() => {
        setIsDismissed(true);
      }, 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 dark:from-green-500/10 dark:to-emerald-500/10 border border-green-500/30 dark:border-green-500/20 rounded-2xl p-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <p className="text-green-700 dark:text-white font-medium">You're on the iOS waitlist!</p>
            <p className="text-green-600/70 dark:text-white/60 text-sm">We'll notify you when it's your turn.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-r from-[#06c]/10 to-purple-500/10 border border-[#06c]/30 dark:border-[#06c]/20 rounded-2xl overflow-hidden mb-6">
      {/* Header - always visible */}
      <div className="p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#06c] to-purple-600 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-[#1d1d1f] dark:text-white font-semibold">QuickMeet for iOS</p>
              <span className="px-2 py-0.5 rounded-full bg-[#06c]/20 text-[#06c] text-xs font-medium">
                Coming Soon
              </span>
            </div>
            <p className="text-[#6e6e73] dark:text-white/60 text-sm">Get early access to our iOS app</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {!isExpanded && (
            <button
              onClick={() => setIsExpanded(true)}
              className="px-4 py-2 rounded-xl bg-[#06c] text-white text-sm font-medium hover:bg-[#0055d4] transition-colors"
            >
              Join Waitlist
            </button>
          )}
          <button
            onClick={handleDismiss}
            className="w-8 h-8 rounded-lg hover:bg-black/5 dark:hover:bg-white/10 flex items-center justify-center transition-colors"
          >
            <svg className="w-4 h-4 text-[#86868b] dark:text-white/40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Expandable form */}
      {isExpanded && (
        <form onSubmit={handleSubmit} className="px-4 pb-4 space-y-3">
          {/* Usage Type Toggle */}
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, usageType: 'personal' }))}
              className={`px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                formData.usageType === 'personal'
                  ? 'bg-[#06c] text-white'
                  : 'bg-black/5 dark:bg-white/5 text-[#1d1d1f] dark:text-white/80 hover:bg-black/10 dark:hover:bg-white/10'
              }`}
            >
              Personal
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, usageType: 'professional' }))}
              className={`px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                formData.usageType === 'professional'
                  ? 'bg-[#06c] text-white'
                  : 'bg-black/5 dark:bg-white/5 text-[#1d1d1f] dark:text-white/80 hover:bg-black/10 dark:hover:bg-white/10'
              }`}
            >
              Professional
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Use Case */}
            <select
              name="useCase"
              value={formData.useCase}
              onChange={handleChange}
              required
              className="w-full px-3 py-2.5 rounded-xl bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 text-[#1d1d1f] dark:text-white text-sm focus:outline-none focus:border-[#06c] appearance-none cursor-pointer"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2386868b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center',
                backgroundSize: '1rem'
              }}
            >
              <option value="" className="bg-white dark:bg-[#1c1c1e]">How will you use it?</option>
              {USE_CASE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value} className="bg-white dark:bg-[#1c1c1e]">
                  {opt.label}
                </option>
              ))}
            </select>

            {/* Industry */}
            <select
              name="industry"
              value={formData.industry}
              onChange={handleChange}
              required
              className="w-full px-3 py-2.5 rounded-xl bg-black/5 dark:bg-white/5 border border-black/10 dark:border-white/10 text-[#1d1d1f] dark:text-white text-sm focus:outline-none focus:border-[#06c] appearance-none cursor-pointer"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2386868b'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'right 0.75rem center',
                backgroundSize: '1rem'
              }}
            >
              <option value="" className="bg-white dark:bg-[#1c1c1e]">Your industry</option>
              {INDUSTRY_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value} className="bg-white dark:bg-[#1c1c1e]">
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {error && (
            <p className="text-red-500 dark:text-red-400 text-xs">{error}</p>
          )}

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-[#06c] to-[#0055d4] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity flex items-center gap-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Joining...
                </>
              ) : (
                'Join Waitlist'
              )}
            </button>
            <button
              type="button"
              onClick={() => setIsExpanded(false)}
              className="text-[#86868b] dark:text-white/40 hover:text-[#1d1d1f] dark:hover:text-white/60 text-sm transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => navigate('/waitlist')}
              className="text-[#06c] hover:text-[#0a84ff] text-sm transition-colors ml-auto"
            >
              Tell us more
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
